import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse } from '../types/api.types';
import {
  KycDocument,
  KycDocumentStatus,
  KycRejectRequest,
  KycSubjectType,
  LicensePricing,
  LicensePricingRequest,
} from '../types/kyc.types';
import { LicenseTier } from '../types/operator.types';

@Injectable({ providedIn: 'root' })
export class KycService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/operator/api`;

  /**
   * Listing admin de **tous** les documents KYC, filtres facultatifs (paginé, 1-indexé).
   * Tri back : `expiryDate` ASC nulls last — les expirations remontent en tête.
   *
   * Filtre omis = pas de filtre. On n'envoie **pas** le param quand il est `null` :
   * le back normalise la chaîne vide, mais autant ne pas la produire.
   */
  list(
    page = 1,
    size = 20,
    kycStatus: KycDocumentStatus | null = null,
    subjectType: KycSubjectType | null = null,
  ): Observable<ApiPageResponse<KycDocument>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (kycStatus) params = params.set('kycStatus', kycStatus);
    if (subjectType) params = params.set('subjectType', subjectType);
    return this.http.get<ApiPageResponse<KycDocument>>(`${this.base}/kyc`, { params });
  }

  /** KYC documents of a specific subject (paginated, 1-indexed). */
  bySubject(
    subjectType: string,
    subjectRef: number,
    page = 1,
    size = 20,
  ): Observable<ApiPageResponse<KycDocument>> {
    const params = new HttpParams()
      .set('subjectType', subjectType)
      .set('subjectRef', String(subjectRef))
      .set('page', String(page))
      .set('size', String(size));
    return this.http.get<ApiPageResponse<KycDocument>>(
      `${this.base}/kyc/subject`,
      { params },
    );
  }

  validate(id: number): Observable<KycDocument> {
    return this.http.post<KycDocument>(`${this.base}/kyc/${id}/validate`, {});
  }

  reject(id: number, body: KycRejectRequest): Observable<KycDocument> {
    return this.http.post<KycDocument>(`${this.base}/kyc/${id}/reject`, body);
  }
}

@Injectable({ providedIn: 'root' })
export class LicensePricingService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/operator/api/admin/license-pricing`;

  /** Admin list of all license pricings (one row per tier). Not paginated. */
  list(): Observable<LicensePricing[]> {
    return this.http.get<LicensePricing[]>(this.base);
  }

  /** Set or update the pricing of a specific tier. */
  set(tier: LicenseTier, body: LicensePricingRequest): Observable<LicensePricing> {
    return this.http.put<LicensePricing>(`${this.base}/${tier}`, body);
  }
}
